import { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Send, User, Mail, FileText, MessageSquare } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";

const SUBJECTS = [
  "Γενική ερώτηση",
  "Καταχώριση επιχείρησης / συλλόγου",
  "Συνεργασία με το SkillCity",
  "Τεχνική υποστήριξη",
  "Πρόταση / παρατήρηση",
];

const fieldCls = "w-full h-[52px] pl-12 pr-4 rounded-xl bg-[#eef3fa] border border-transparent focus:border-primary/40 focus:bg-white outline-none transition-colors text-[15px]";

const Label = ({ children }) => (
  <label className="block text-[15px] font-medium text-slate-900 mb-2">{children} <span className="text-red-500">*</span></label>
);

const IconInput = ({ icon: Icon, ...props }) => (
  <div className="relative">
    <Icon size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-primary" />
    <input required {...props} className={fieldCls} />
  </div>
);

export const ContactForm = () => {
  const location = useLocation();
  const [form, setForm] = useState({ name: "", email: "", subject: location.state?.partner ? SUBJECTS[2] : "", message: "" });
  const [loading, setLoading] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject) return toast.error("Επιλέξτε θέμα");
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Το μήνυμά σας στάλθηκε! Θα επικοινωνήσουμε σύντομα.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Αποτυχία αποστολής");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="contact-form-container">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label>Όνομα</Label><IconInput icon={User} data-testid="contact-name-input" value={form.name} onChange={(e) => update("name", e.target.value)} /></div>
        <div><Label>Email</Label><IconInput icon={Mail} type="email" data-testid="contact-email-input" value={form.email} onChange={(e) => update("email", e.target.value)} /></div>
      </div>
      <div>
        <Label>Θέμα</Label>
        <div className="flex gap-0 items-stretch">
          <span className="w-14 shrink-0 grid place-items-center rounded-l-xl bg-[#eef3fa] text-primary"><FileText size={20} /></span>
          <Select value={form.subject} onValueChange={(v) => update("subject", v)}>
            <SelectTrigger data-testid="contact-subject-select" className="h-[52px] flex-1 rounded-l-none rounded-r-xl bg-[#eef3fa] border-0 border-l-2 border-white text-[15px] focus:ring-0 focus:ring-offset-0 [&>svg]:text-primary [&>svg]:opacity-100 [&>svg]:h-5 [&>svg]:w-5">
              <SelectValue placeholder="Επιλέξτε θέμα" />
            </SelectTrigger>
            <SelectContent position="popper" sideOffset={4} className="rounded-xl bg-white border-slate-100 shadow-xl p-1.5 w-[var(--radix-select-trigger-width)]">
              {SUBJECTS.map((s, i) => (
                <SelectItem key={s} value={s} data-testid={`contact-subject-option-${i}`} className="rounded-md py-1.5 pl-3 text-[15px] cursor-pointer text-slate-800 focus:bg-sky-100 focus:text-slate-900 data-[state=checked]:bg-sky-100 [&>span:first-child]:hidden">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>Μήνυμα</Label>
        <div className="relative">
          <MessageSquare size={20} className="absolute left-4 top-4 text-primary" />
          <textarea required rows={3} placeholder="Γράψτε το μήνυμά σας..." data-testid="contact-message-input" value={form.message} onChange={(e) => update("message", e.target.value)}
            className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-[#eef3fa] border border-transparent focus:border-primary/40 focus:bg-white outline-none transition-colors resize-none text-[15px] placeholder:text-slate-400" />
        </div>
      </div>
      <button type="submit" disabled={loading} data-testid="contact-submit-button"
        className="inline-flex items-center gap-3 h-[60px] px-9 rounded-full bg-primary text-white text-[17px] font-medium shadow-lg shadow-primary/30 hover:-translate-y-0.5 hover:shadow-xl transition-[transform,box-shadow] disabled:opacity-60">
        <Send size={20} /> {loading ? "Αποστολή..." : "Στείλτε το μήνυμά σας"}
      </button>
    </form>
  );
};
